import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  ViewChild,
  AfterContentInit,
  Output,
  NgZone,
  AfterContentChecked,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  Renderer2,
  signal,
  WritableSignal,
  OnInit,
  computed,
} from '@angular/core';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { IonButton, IonCheckbox, IonIcon, IonSpinner } from '@ionic/angular/standalone';
import { CommonModule } from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import { jsonCopy } from '@app/utils/app.utils';
import { NbsSelectDropdownOptionComponent } from './nbs-select-dropdown-option.component';
import { FilterOption, SelectAllStatus } from '@app/types/filter.types';

@Component({
  selector: 'app-nbs-select-dropdown',
  templateUrl: './nbs-select-dropdown.component.html',
  styleUrls: ['./nbs-select-dropdown.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonSpinner,
    IonIcon,
    CommonModule,
    IonButton,
    IonCheckbox,
    ScrollingModule,
    NbsSelectDropdownOptionComponent,
  ],
  standalone: true,
})
export class NbsSelectDropdownComponent
  implements AfterViewInit, OnDestroy, AfterContentInit, AfterContentChecked, OnChanges
{
  @Input() label: string = '';
  @Input() class?: string;
  @Input() multiSelect: boolean = false;
  @Input() disabled: boolean = false;
  @Input() searchable?: boolean = false;
  @Input() enableSelectAll?: boolean = false;
  @Input() value: any = [];
  @Input() enableClearAll: boolean = false;
  @Input() isRequired: boolean = false;
  @Input() isFormSubmitted: boolean = false;
  @Input() showLoader: boolean = false;
  @Input() placeholder?: string;
  @Input() options: FilterOption[] = [];
  @Output() selectedItemChange: EventEmitter<string | string[]> = new EventEmitter();
  @Output() searchChange = new EventEmitter<any>();

  @ViewChild('labelRef') labelRef!: ElementRef;
  @Output() selectionChange = new EventEmitter<any>();
  @Output() onOptionTrayClose = new EventEmitter<any>();
  @ViewChild('labelNotchRef') labelNotchRef!: ElementRef;
  @ViewChild('dropDownRef') dropDownRef!: ElementRef;
  @ViewChild('dropdownHolderRef') dropdownHolderRef!: ElementRef;
  @ViewChild('nbsSelectRef') nbsSelectRef!: ElementRef<HTMLDivElement>;
  @ViewChild('contentRef') contentRef!: any;
  @ViewChild('searchBoxRef') searchBoxRef!: ElementRef<HTMLInputElement>;
  @ViewChild('clearBtnRef') clearBtnRef!: any;

  private resizeObserver!: ResizeObserver;

  isDropdownOpen: WritableSignal<boolean> = signal<boolean>(false);
  selectedOptions: WritableSignal<any> = signal<any>([]);
  private _allOptions: WritableSignal<FilterOption[]> = signal<FilterOption[]>([]); // Internal signal for all options
  private _searchTerm: WritableSignal<string> = signal<string>(''); // New signal for search term

  filterOptions = computed(() => {
    const term = this._searchTerm().toLowerCase();
    if (!term) {
      return this._allOptions();
    }
    return this._allOptions().filter((option) => (option?.label?.toLowerCase() || '').includes(term));
  });

  itemSize: WritableSignal<number> = signal<number>(30);
  minBufferPx: WritableSignal<number> = signal<number>(900);
  maxBufferPx: WritableSignal<number> = signal<number>(2000);

  selectAllStatus = computed(() => {
    const currentFilteredOptions = this.filterOptions();
    const currentlySelectedValues = this.selectedOptions();

    if (currentFilteredOptions.length === 0) {
      return SelectAllStatus.unchecked;
    }

    const allFilteredSelected = currentFilteredOptions.every((option) =>
      currentlySelectedValues.includes(option.value),
    );
    const anyFilteredSelected = currentFilteredOptions.some((option) => currentlySelectedValues.includes(option.value));

    if (allFilteredSelected) {
      return SelectAllStatus.checked;
    } else if (anyFilteredSelected) {
      return SelectAllStatus.indeterminate;
    } else {
      return SelectAllStatus.unchecked;
    }
  });

  private resizeSubject = new Subject<DOMRectReadOnly>();
  subscriptions: Subscription[] = [];
  private lastLabelNotchWidth: number = -1;

  constructor(
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.['options']?.currentValue) {
      this._allOptions.set(structuredClone(changes?.['options']?.currentValue));
    }
    if (
      changes['value'] &&
      JSON.stringify(changes['value'].previousValue) !== JSON.stringify(changes['value'].currentValue)
    ) {
      this.selectedOptions.set(structuredClone(changes['value'].currentValue));
    }
  }

  ngAfterContentChecked(): void {}

  ngAfterViewInit(): void {
    // Ensure viewport size is correct
    this.contentRef.checkViewportSize();

    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => this.updateLabelNotch());
      });

      if (this.labelRef?.nativeElement) {
        this.resizeObserver.observe(this.labelRef.nativeElement);
      }
    });

    // Initial call
    this.updateLabelNotch();
  }

  private updateLabelNotch(): void {
    if (!this.label || !this.labelRef?.nativeElement || !this.labelNotchRef?.nativeElement) return;

    const labelWidth = this.labelRef.nativeElement.offsetWidth;
    const newWidth = labelWidth + 12;

    // Only update if changed to avoid DOM thrashing
    if (this.lastLabelNotchWidth !== newWidth) {
      this.renderer.setStyle(this.labelNotchRef.nativeElement, 'width', `${newWidth}px`);
      this.lastLabelNotchWidth = newWidth;
    }
  }

  ngAfterContentInit(): void {}

  onItemSelection({ value }: any) {
    if (!this.multiSelect) {
      this.selectedOptions.set([value]);
      this.selectedItemChange.emit(this.selectedOptions());
      this.handleClickOutside(new MouseEvent('click')); // Close dropdown for single select
    } else {
      let selectedOptionsCopy = jsonCopy(this.selectedOptions());
      const selectedValueIndex = selectedOptionsCopy.indexOf(value);

      if (selectedValueIndex > -1) {
        selectedOptionsCopy.splice(selectedValueIndex, 1);
      } else {
        selectedOptionsCopy = [...selectedOptionsCopy, value];
      }
      this.selectedOptions.set(selectedOptionsCopy);
      this.selectedItemChange.emit(this.selectedOptions());
    }
  }

  openDropDown(event: MouseEvent) {
    event.stopPropagation();
    if (this.disabled || this.showLoader) {
      return;
    }

    this.isDropdownOpen.set(true);
    const element = this.nbsSelectRef.nativeElement;
    this.dropdownHolderRef.nativeElement.style.display = 'block';

    this.renderer.appendChild(document.body, this.dropdownHolderRef.nativeElement);

    const rect = element.getBoundingClientRect();
    this.dropDownRef.nativeElement.style.left = `${rect.left}px`;
    this.dropDownRef.nativeElement.style.width = `${rect.width}px`;

    const bodyHeight = document.body.clientHeight;
    const height = this.nbsSelectRef.nativeElement.offsetHeight;
    const top = this.nbsSelectRef.nativeElement.offsetTop;

    if (bodyHeight / 2 < top + height + 2) {
      this.dropDownRef.nativeElement.style.top = undefined;
      this.dropDownRef.nativeElement.style.bottom = `${bodyHeight - rect.top + 8}px`;

      const maxHeight = bodyHeight - (bodyHeight - (rect.top - rect.height - 38));
      this.minBufferPx.set(maxHeight);
      this.maxBufferPx.set(maxHeight + 10 * 30);
      const optionsTrayHeight = this.options.length * 30 + 48.68;
      const height = optionsTrayHeight > maxHeight ? maxHeight : optionsTrayHeight;

      this.contentRef.elementRef.nativeElement.style.maxHeight = `${maxHeight}px`;
      this.contentRef.elementRef.nativeElement.style.height = `${height}px`;
    } else {
      this.dropDownRef.nativeElement.style.bottom = undefined;
      this.dropDownRef.nativeElement.style.top = `${rect.bottom + 4}px`;

      const maxHeight = bodyHeight - (rect.top + rect.height + 12 + 78.67);
      this.minBufferPx.set(maxHeight + 50);
      this.maxBufferPx.set(maxHeight + 10 * 30);
      const optionsTrayHeight = this.options.length * 30 + 48.68;
      const height = optionsTrayHeight > maxHeight ? maxHeight : optionsTrayHeight;

      this.contentRef.elementRef.nativeElement.style.maxHeight = `${maxHeight}px`;
      this.contentRef.elementRef.nativeElement.style.height = `${height}px`;
    }
  }

  handleClickOutside(event: MouseEvent) {
    event.stopPropagation();
    if (this.dropdownHolderRef) {
      this.renderer.removeChild(document.body, this.dropdownHolderRef.nativeElement);
    }
    this.isDropdownOpen.set(false); // Close the dropdown if clicked outside
    this.dropdownHolderRef.nativeElement.style.display = 'none';

    this.onOptionTrayClose.emit(this.selectedOptions());
    // Clear search term when closing dropdown
    this._searchTerm.set('');
    // Reset the input field value
    if (this.searchBoxRef) {
      this.searchBoxRef.nativeElement.value = '';
    }
  }

  getMainClasses() {
    return {
      active: this.isDropdownOpen(),
      ['' + this.class?.toString()]: this.class ? true : false,
      disabled: this.disabled,
    };
  }

  preventCheckbox(event: Event) {
    // event.preventDefault();
    event.stopPropagation();
  }

  onSearchChangeLocal(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this._searchTerm.set(inputElement.value);
    this.searchChange.emit(inputElement.value); // Emit search term if needed by parent
  }

  onClearSelection(event: MouseEvent) {
    event.stopPropagation();
    const buttonEl = this.clearBtnRef.el as unknown as HTMLElement;
    if (buttonEl) {
      buttonEl.classList.add('active'); // ✅ Add class dynamically
      setTimeout(() => {
        this.selectedOptions.set([]);
        this.selectedItemChange.emit(this.selectedOptions());
        this.onOptionTrayClose.emit(this.selectedOptions());
      }, 1000);
    }
  }

  getSelectAllIndeterminateStatus() {
    return this.selectAllStatus() === SelectAllStatus.indeterminate;
  }

  getSelectAllStatus() {
    //IMP is responsible to show unchecked after the indeterminate is clicked and it deselects the records.
    if (this.selectedOptions().length > 0) return true;
    else return false;
  }

  onSelectAll(event: MouseEvent) {
    event.stopPropagation();

    const currentFilteredOptions = this.filterOptions(); // Get the options currently visible
    let currentSelectedValues = new Set(this.selectedOptions()); // Use a Set for efficient operations
    const anyFilteredSelected = currentFilteredOptions.some((option) => this.selectedOptions().includes(option.value));

    // Determine if all currently filtered options are already selected.
    // This is the key to deciding whether to select or deselect the filtered set.
    const allFilteredAreCurrentlySelected = currentFilteredOptions.every((option) =>
      currentSelectedValues.has(option.value),
    );

    if (allFilteredAreCurrentlySelected) {
      // If all filtered options are currently selected, the user's intent is to DESELECT them.
      currentFilteredOptions.forEach((option) => {
        currentSelectedValues.delete(option.value);
      });
    } else if (!allFilteredAreCurrentlySelected && anyFilteredSelected) {
      currentFilteredOptions.forEach((option) => {
        currentSelectedValues.delete(option.value);
      });
    } else {
      // If some or none of the filtered options are currently selected, the user's intent is to SELECT them.
      currentFilteredOptions.forEach((option) => {
        currentSelectedValues.add(option.value);
      });
    }

    this.selectedOptions.set(Array.from(currentSelectedValues)); // Update the main selection signal
    this.selectedItemChange.emit(this.selectedOptions()); // Emit the change
  }

  isSelectedOption(value: string | number) {
    return this.selectedOptions().includes(value);
  }

  onCheckboxClick(event: any) {
    event.preventDefault(); // Prevents default behavior
    event.stopPropagation(); // Stops event bubbling
  }

  isValueArray(): boolean {
    return Array.isArray(this.selectedOptions) && this.selectedOptions.length > 0;
  }

  isValueSet(): boolean {
    return this.selectedOptions() && this.selectedOptions().length > 0 && this.enableClearAll;
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  getDisplayValue() {
    if (
      this.multiSelect && //added this to fix OR selection displaying
      this.selectedOptions()?.length === this._allOptions().length &&
      this._allOptions().length > 0
    ) {
      return 'All';
    }
    if (this.selectedOptions()) {
      if (Array.isArray(this.selectedOptions())) {
        const list = this.selectedOptions()
          .slice(0, 6)
          .map((value: string | number) => {
            const foundOptions = this._allOptions().find((option: FilterOption) => {
              return option.value.toString() === value.toString();
            });
            return foundOptions?.label;
          })
          .filter((label: any) => label !== undefined); // Filter out undefined labels
        return list.join(', ');
      }

      const foundOptions = this._allOptions().find((option: FilterOption) => {
        return option.value === this.selectedOptions();
      });

      return foundOptions?.label; // TODO modified this to show lables on the user settings panel
    }
    return '';
  }

  trackByFilterOptionKey(index: number, item: any): string {
    return item.value.toString(); // Ensure 'filter_key' is unique for each filter
  }
}
